import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
import { AdminProvider } from './contexts/AdminContext';
import AuthGuard from './components/admin/AuthGuard';
import AdminLayout from './components/admin/AdminLayout';
import { useRedirects } from './hooks/useRedirects';

// Lazy load components for better performance
const Home = React.lazy(() => import('./pages/Home'));
const About = React.lazy(() => import('./pages/About'));
const Services = React.lazy(() => import('./pages/Services'));
const Portfolio = React.lazy(() => import('./pages/Portfolio'));
const Results = React.lazy(() => import('./pages/Results'));
const Blog = React.lazy(() => import('./pages/Blog'));
const BlogPost = React.lazy(() => import('./pages/BlogPost'));
const Contact = React.lazy(() => import('./pages/Contact'));
const Resources = React.lazy(() => import('./pages/Resources'));
const Demo = React.lazy(() => import('./pages/Demo'));

// Admin components
const AdminLogin = React.lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const PostsList = React.lazy(() => import('./pages/admin/PostsList'));
const PostEditor = React.lazy(() => import('./pages/admin/PostEditor'));
const CategoriesManager = React.lazy(() => import('./pages/admin/CategoriesManager'));
const RedirectsManager = React.lazy(() => import('./pages/admin/RedirectsManager'));
const UsersList = React.lazy(() => import('./pages/admin/UsersList'));
const UserEditor = React.lazy(() => import('./pages/admin/UserEditor'));
const UserProfile = React.lazy(() => import('./pages/admin/UserProfile'));
const AdminProfile = React.lazy(() => import('./pages/admin/AdminProfile'));

// Component that uses redirects hook (must be inside Router)
const AppContent: React.FC = () => {
  // Initialize redirects handling
  useRedirects();
  const location = useLocation();
  
  // Check if current route is admin route
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <div className="min-h-screen bg-light">
      {/* Only show Navbar and Footer for public pages */}
      {!isAdminRoute && <Navbar />}
      <main>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/results" element={<Results />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/demo" element={<Demo />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={
              <AuthGuard>
                <AdminProvider>
                  <AdminLayout />
                </AdminProvider>
              </AuthGuard>
            }>
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="posts" element={<PostsList />} />
              <Route path="posts/new" element={<PostEditor />} />
              <Route path="posts/:id/edit" element={<PostEditor />} />
              <Route path="categories" element={<CategoriesManager />} />
              <Route path="redirects" element={<RedirectsManager />} />
              <Route path="users" element={<UsersList />} />
              <Route path="users/new" element={<UserEditor />} />
              <Route path="users/:id/edit" element={<UserEditor />} />
              <Route path="users/:id/profile" element={<UserProfile />} />
              <Route path="profile" element={<AdminProfile />} />
              <Route index element={<AdminDashboard />} />
            </Route>
          </Routes>
        </Suspense>
      </main>
      {!isAdminRoute && <Footer />}
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <Router>
        <ErrorBoundary>
          <AppContent />
        </ErrorBoundary>
      </Router>
    </HelmetProvider>
  );
}

export default App; 